import { yupResolver } from '@hookform/resolvers/yup'
import { CheckCircle, XCircle } from 'phosphor-react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CurrencyInput } from 'react-currency-mask'
import { Button } from '../../../../../../components/Button'
import { Input } from '../../../../../../components/FormElements/Input'
import { Radio } from '../../../../../../components/Radio'
import { useExpertProfessionalDataMutation } from '../../../../../../services/hooks/experts/mutations/useExpertProfissionalData'
import { type ExpertDomain } from '../../../../../../types/domain/expert-domain'
import { type ProfessionalDataPersistence } from '../../../../../../types/persistence/professional-data-persistence'
import { getErrorMessage } from '../../../../../../utils/getErrorMessage'
import { professionalDataSchema } from '../../../../../../validators/profissionalDataSchema'
import { Select } from '../../../../../../components/Primitives/Select'
import { FormGroup } from '../../../../../../components/FormElements/FormGroup'
import { Textarea } from '../../../../../../components/FormElements/Textarea'

interface Props {
  data?: ExpertDomain
  save: () => void
}

export const ProfessionalForm = ({ data, save }: Props) => {
  const { mutate, isLoading } = useExpertProfessionalDataMutation()

  const blindDefaultValue =
    typeof data?.professionalData?.blind !== 'boolean'
      ? ''
      : data.professionalData.blind
      ? 'yes'
      : 'no'

  const currencyOptions = ['BRL', 'USD', 'EUR'].map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const { control, handleSubmit, watch } = useForm<ProfessionalDataPersistence>(
    {
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(professionalDataSchema),
      defaultValues: {
        previousCompany: data?.professionalData?.previousCompany,
        currentCompany: data?.professionalData?.currentCompany,
        position: data?.professionalData?.position,
        sector: data?.professionalData?.sector,
        subsector: data?.professionalData?.subsector,
        area: data?.professionalData?.area,
        value: data?.professionalData?.value,
        currency: data?.professionalData?.currency,
        smallGroup: data?.professionalData?.smallGroup,
        blind: blindDefaultValue,
        proactive: data?.professionalData?.proactive,
        bio: data?.bio,
        observations: data?.observations,
      },
    }
  )

  const editProfissionalData = (info: ProfessionalDataPersistence) => {
    if (data?.id) {
      mutate(
        { id: data?.id, data: info },
        {
          onSuccess() {
            toast('Dados profissionais salvos com sucesso!', {
              position: 'top-right',
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              closeButton: false,
              icon: (
                <CheckCircle
                  size={48}
                  className="mx-auto my-auto text-alert-success"
                />
              ),
              className: 'bg-green-50',
            })
            save()
          },
          onError(error: unknown) {
            toast(getErrorMessage(error), {
              position: 'top-right',
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              closeButton: false,
              icon: (
                <XCircle
                  size={48}
                  className="mx-auto my-auto text-alert-error"
                />
              ),
              className: 'bg-red-50',
            })
          },
        }
      )
    }
  }

  return (
    <form onSubmit={handleSubmit(editProfissionalData)}>
      <div className="w-full max-w-[640px] flex flex-col gap-[24px]">
        <Controller
          name="bio"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Textarea
              label="Bio"
              placeholder="Bio"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="observations"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Observações"
              placeholder="Observações"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="previousCompany"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Empresa anterior"
              placeholder="Empresa anterior"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="currentCompany"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Empresa atual"
              placeholder="Empresa atual"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="position"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Cargo"
              placeholder="Cargo"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="sector"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Setor"
              placeholder="Setor"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="subsector"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Subsetor"
              placeholder="Subsetor"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="area"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="Área"
              placeholder="Área"
              isValid={false}
              value={value || ''}
              onChange={onChange}
              errorMessage={error?.message && `${error?.message}`}
            />
          )}
        />

        <Controller
          name="currency"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup label="Moeda">
                <Select
                  defaultValue={value ?? ''}
                  onValueChange={(value) => onChange(value)}
                  options={currencyOptions}
                />
              </FormGroup>
            )
          }}
        />

        <Controller
          name="value"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <CurrencyInput
                value={Number(value)}
                onChangeValue={(_: any, value: any) => {
                  onChange(value)
                }}
                currency={watch('currency') ?? undefined}
                InputElement={
                  <Input
                    onInput={(event) => {
                      const { value } = event.target as HTMLInputElement

                      if (!value.trim()) {
                        onChange(event)
                      }
                    }}
                    label="Valor"
                    placeholder="0,00"
                    isValid={false}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                }
              />
            )
          }}
        />

        <Controller
          name="smallGroup"
          control={control}
          render={({ field: { onChange } }) => (
            <Radio.Root
              name="smallGroup"
              onChange={onChange}
              defaultValue={data?.professionalData?.smallGroup}
            >
              <p className="text-xs lg:text-base">Small Group:</p>
              <div className="flex gap-10 text-xs mt-7 lg:text-base flex-wrap">
                <Radio.Item id="x1_smallGroup" value="x1">
                  <Radio.Label htmlFor="x1_smallGroup">x1</Radio.Label>
                </Radio.Item>

                <Radio.Item id="x2_smallGroup" value="x2">
                  <Radio.Label htmlFor="x2_smallGroup">x2</Radio.Label>
                </Radio.Item>

                <Radio.Item id="x3_smallGroup" value="x3">
                  <Radio.Label htmlFor="x3_smallGroup">x3</Radio.Label>
                </Radio.Item>

                <Radio.Item id="x4_smallGroup" value="x4">
                  <Radio.Label htmlFor="x4_smallGroup">x4</Radio.Label>
                </Radio.Item>

                <Radio.Item id="x5_smallGroup" value="x5">
                  <Radio.Label htmlFor="x5_smallGroup">x5</Radio.Label>
                </Radio.Item>

                <Radio.Item id="moreThan5_smallGroup" value="moreThan5">
                  <Radio.Label htmlFor="moreThan5_smallGroup">
                    Mais de 5
                  </Radio.Label>
                </Radio.Item>
              </div>
            </Radio.Root>
          )}
        />

        <Controller
          name="blind"
          control={control}
          render={({ field: { onChange } }) => (
            <Radio.Root
              name="blind"
              onChange={onChange}
              defaultValue={blindDefaultValue}
            >
              <p className="text-xs lg:text-base">Blind:</p>
              <div className="flex gap-10 text-xs mt-7 lg:text-base">
                <Radio.Item id="no_blind" value="no">
                  <Radio.Label htmlFor="no_blind">Não</Radio.Label>
                </Radio.Item>

                <Radio.Item id="yes_blind" value="yes">
                  <Radio.Label htmlFor="yes_blind">Sim</Radio.Label>
                </Radio.Item>
              </div>
            </Radio.Root>
          )}
        />

        <Controller
          name="proactive"
          control={control}
          render={({ field: { onChange } }) => (
            <Radio.Root
              name="proactive"
              onChange={onChange}
              defaultValue={data?.professionalData?.proactive}
            >
              <p className="text-xs lg:text-base">Proativo:</p>
              <div className="flex gap-10 text-xs mt-7 lg:text-base flex-wrap">
                <Radio.Item id="no_proactive" value="no">
                  <Radio.Label htmlFor="no_proactive">Não</Radio.Label>
                </Radio.Item>

                <Radio.Item id="yes_search" value="yesSearch">
                  <Radio.Label htmlFor="yes_search">Sim, pesquisa</Radio.Label>
                </Radio.Item>

                <Radio.Item id="yes_offer" value="yesOffer">
                  <Radio.Label htmlFor="yes_offer">Sim, oferta</Radio.Label>
                </Radio.Item>
              </div>
            </Radio.Root>
          )}
        />
      </div>

      <div className="flex flex-row gap-2 mt-14">
        <Button type="submit" isLoading={isLoading}>
          Salvar
        </Button>
      </div>
    </form>
  )
}
